import { useEffect, useState } from 'react'

/**
 * Slow-rotating prompt hints below the voice button. Each line cross-fades
 * in/out over ~5 seconds so the user gets a sense of *what to say*
 * without ever feeling rushed or quizzed. Calm, not distracting — the
 * Shazam-style "we got this" energy applied to a conversational form.
 *
 * Hints are context-aware:
 *   - Pre-session (no sessionId): broad orientation prompts
 *   - Mid-session: tilted toward whichever 5-slot is still empty,
 *     so the rotation actively nudges what to say next
 */

import type { ExtractedItem } from '../lib/api'

const LOST_HINTS_INITIAL = [
  'What was the item — color, brand, kind?',
  'Where did you lose it? Venue, city, state.',
  'Was this today? About what time?',
  'Anything special about it — marks, stickers, a case?',
  'Was anything inside it — receipts, cards, photos?',
]

const FOUND_HINTS_INITIAL = [
  'What did you find — color, brand, kind?',
  'Where exactly was it found?',
  'About when was this?',
  'Anything distinguishing about it?',
  'Are you logging this for a venue?',
]

const FOLLOWUP_HINTS = {
  what: 'What kind of item — color, brand?',
  where: 'Where — venue, city?',
  when: 'When did this happen?',
  who: 'Your name and how to reach you?',
  help: 'Anything special about it — marks, stickers?',
}

interface RotatingHintsProps {
  reportType: 'lost' | 'found'
  item: Partial<ExtractedItem>
  active: boolean // false to pause rotation (e.g. while recording)
}

function pickHints(reportType: 'lost' | 'found', item: Partial<ExtractedItem>): string[] {
  // No data yet — broad orientation set
  const hasAny =
    !!item.category || !!item.venue_name || !!item.date_occurred ||
    !!item.reporter_name || !!item.distinguishing_marks
  if (!hasAny) {
    return reportType === 'lost' ? LOST_HINTS_INITIAL : FOUND_HINTS_INITIAL
  }

  // Otherwise, surface a hint per still-empty slot
  const out: string[] = []
  if (!item.category && !item.brand) out.push(FOLLOWUP_HINTS.what)
  if (!item.venue_name) out.push(FOLLOWUP_HINTS.where)
  if (!item.date_occurred) out.push(FOLLOWUP_HINTS.when)
  if (!item.reporter_name && !item.reporter_email && !item.reporter_phone)
    out.push(FOLLOWUP_HINTS.who)
  if (!item.distinguishing_marks) out.push(FOLLOWUP_HINTS.help)

  // If everything's filled, fall back to a quiet single line
  if (out.length === 0) {
    return ['Anything else worth mentioning?']
  }
  return out
}

export default function RotatingHints({ reportType, item, active }: RotatingHintsProps) {
  const hints = pickHints(reportType, item)
  const [idx, setIdx] = useState(0)

  useEffect(() => {
    if (!active || hints.length <= 1) return
    // Slower rotation matches the soothier hint-fade cycle (6.5s) — per
    // Ryan: "make the rotating words animation more soothing and
    // assuring." 5500ms gives the user time to read each prompt and
    // mentally answer it before the next one cycles in.
    const id = setInterval(() => {
      setIdx((i) => (i + 1) % hints.length)
    }, 5500)
    return () => clearInterval(id)
  }, [active, hints.length])

  // Reset when the hint set changes (e.g., a slot just filled)
  useEffect(() => {
    setIdx(0)
  }, [hints.join('|')])

  if (!active) return null

  // Reserve a fixed two-line height — the prompts vary between one and
  // two lines on narrow viewports, and a min-height that grew with the
  // text was shifting the entire page when the rotation cycled. Locking
  // the box at ~2 lines (font 18 × line-height 1.4 = 25.2 × 2 ≈ 50px)
  // keeps everything below in place.
  return (
    <div
      className="text-center px-6 max-w-xl mx-auto flex items-center justify-center"
      style={{
        color: 'var(--ink-3)',
        height: '3.2rem',
      }}
      aria-live="polite"
    >
      <span
        key={idx + ':' + hints[idx]}
        className="inline-block animate-hint italic"
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: '18px',
          lineHeight: 1.4,
          letterSpacing: '-0.005em',
        }}
      >
        {hints[idx]}
      </span>
    </div>
  )
}
